import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Printer, Wifi, Edit, Trash2, CheckCircle2, XCircle, Loader2, Download, ChefHat, Receipt as ReceiptIcon, Tag as TagIcon, Info, Zap, GlassWater, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isBridgeAlive, pingPrinter, isLikelyIOS } from '@/lib/printBridge';
import QuickPrinterSetupDialog from './QuickPrinterSetupDialog';
import PrinterSettingsHelpPanel from './PrinterSettingsHelpPanel';

// Detect the device family once so we can tailor the install advice. We
// don't gate any behaviour on this — just show the right tip card.
function detectDevice() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const isIPad = /iPad/.test(ua) || (/Mac/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  if (isIPad) return 'ipad';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/Android/.test(ua)) return 'android';
  if (/Macintosh/.test(ua)) return 'mac';
  if (/Windows/.test(ua)) return 'windows';
  return 'desktop';
}

/**
 * ESC/POS printer management dialog — add/edit/test/ping/delete per-branch
 * printers (Cashier receipt + Kitchen + Label). Parent owns the form +
 * list state; this component is pure UI + bridge probe.
 */
export default function PrinterSettingsDialog({
  open,
  onOpenChange,
  printers,
  printerForm,
  setPrinterForm,
  editingPrinter,
  setEditingPrinter,
  savePrinter,
  deletePrinter,
  testPrinter,
}) {
  const [branches, setBranches] = useState([]);
  // iter648 — On iOS the local-bridge probe will ALWAYS fail (no Python
  // runtime on iPad). Skip it and surface the Cloud Relay status instead.
  const iOSMode = isLikelyIOS();
  const [bridgeAlive, setBridgeAlive] = useState(iOSMode ? 'ipad' : null); // null=unknown, true/false, 'ipad'
  const [pingingId, setPingingId] = useState(null);
  const [pingResults, setPingResults] = useState({}); // {printerId: 'ok' | 'fail'}
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const device = detectDevice();
  // iter648 — Track cloud bridge online state for the iPad banner.
  const [cloudBridgeOnline, setCloudBridgeOnline] = useState(null);

  // Probe bridge whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (iOSMode) {
      // On iPad we ask the cloud whether ANY Bridge is registered + online.
      (async () => {
        try {
          const { data } = await api.get('/cashier/print-bridge/bridges');
          setCloudBridgeOnline((data.bridges || []).some(b => b.online));
        } catch {
          setCloudBridgeOnline(false);
        }
      })();
      return;
    }
    let cancelled = false;
    (async () => {
      const alive = await isBridgeAlive();
      if (!cancelled) setBridgeAlive(alive);
    })();
    return () => { cancelled = true; };
  }, [open, iOSMode]);

  // Fetch branches once on open for the branch selector.
  const reloadBranches = async () => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const { data } = await api.get('/branches', { headers: { Authorization: `Bearer ${token}` } });
      setBranches(Array.isArray(data) ? data : []);
    } catch { setBranches([]); }
  };
  useEffect(() => {
    if (!open) return;
    (async () => { await reloadBranches(); })();
  }, [open]);

  const handlePing = async (p) => {
    if (!p.ip_address) { toast.error('No IP address configured'); return; }
    if (iOSMode) {
      toast.info('Ping is a local-LAN test. On iPad, send a real "Test print" instead — it goes via Cloud Relay.');
      return;
    }
    if (!bridgeAlive) { toast.error('Print bridge not running — install it first.'); return; }
    setPingingId(p.id);
    const r = await pingPrinter(p.ip_address, p.port || 9100);
    setPingingId(null);
    setPingResults((m) => ({ ...m, [p.id]: r.ok ? 'ok' : 'fail' }));
    if (r.ok) toast.success(`${p.name} reachable at ${p.ip_address}`);
    else toast.error(`${p.name} not reachable — check IP, port, network.`);
  };

  const downloadBridge = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cashier/print-bridge/download`;
    window.open(url, '_blank');
  };

  // iter616 — Per-branch quick-actions for Print mode + Training mode.
  // We expose them inline so the manager doesn't have to leave the
  // printer dialog. They apply to whichever branch is currently
  // selected in the form's branch dropdown.
  const selectedBranchId = printerForm.branch_id || null;
  const selectedBranch = branches.find(b => b.id === selectedBranchId) || null;
  const branchDefaultMode = selectedBranch?.default_print_mode || 'auto';
  const trainingUntil = selectedBranch?.training_mode_until || null;
  const trainingActive = !!(trainingUntil && new Date(trainingUntil) > new Date());

  const refreshBranches = async () => {
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      const { data } = await api.get('/branches', { headers: { Authorization: `Bearer ${token}` } });
      setBranches(Array.isArray(data) ? data : []);
    } catch { /* */ }
  };
  const updateBranchDefaultMode = async (newMode) => {
    if (!selectedBranchId) { toast.error('Pick a branch above first.'); return; }
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      await api.put(`/branches/${selectedBranchId}/printer-defaults`, { default_print_mode: newMode }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Branch default print mode → ${newMode}`);
      await refreshBranches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update branch default'); }
  };
  const toggleTrainingMode = async () => {
    if (!selectedBranchId) { toast.error('Pick a branch above first.'); return; }
    try {
      const token = localStorage.getItem('cashier_token') || localStorage.getItem('token');
      // 60 min on first toggle, 0 to disable.
      const minutes = trainingActive ? 0 : 60;
      await api.post(`/branches/${selectedBranchId}/training-mode`, { minutes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(minutes > 0
        ? `Training mode ON for the next ${minutes} min — every print opens the OS dialog.`
        : 'Training mode OFF — back to normal printing.');
      await refreshBranches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle training mode'); }
  };

  const typeIcon = (t) => {
    if (t === 'kitchen') return <ChefHat size={14} />;
    if (t === 'bar') return <GlassWater size={14} />;
    if (t === 'label') return <TagIcon size={14} />;
    return <ReceiptIcon size={14} />;
  };
  const typeBadge = (t) => {
    if (t === 'kitchen') return 'bg-orange-100 text-orange-700';
    if (t === 'bar') return 'bg-cyan-100 text-cyan-700';
    if (t === 'label') return 'bg-slate-100 text-slate-700';
    return 'bg-violet-100 text-violet-700';
  };
  const typeLabel = (t) => {
    if (t === 'receipt') return 'Cashier';
    if (t === 'kitchen') return 'Kitchen';
    if (t === 'bar') return 'Bar';
    return t;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="printer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Printer size={18} className="text-primary" />
              Printer Settings
            </span>
            <Button
              size="sm"
              className="rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
              onClick={() => setShowQuickSetup(true)}
              data-testid="open-quick-setup-btn"
            >
              <Zap size={13} className="mr-1" />
              Quick branch setup
            </Button>
          </DialogTitle>
        </DialogHeader>

        {/* Bridge status banner */}
        <div className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-xs ${
          iOSMode && cloudBridgeOnline ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : iOSMode && cloudBridgeOnline === false ? 'bg-rose-50 text-rose-800 border border-rose-200'
          : iOSMode ? 'bg-sky-50 text-sky-700 border border-sky-200'
          : bridgeAlive === true ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : bridgeAlive === false ? 'bg-amber-50 text-amber-700 border border-amber-200'
          : 'bg-slate-50 text-slate-600 border border-slate-200'
        }`}
          data-testid="bridge-status-banner"
        >
          <div className="flex items-center gap-2">
            {iOSMode
              ? (cloudBridgeOnline ? <CheckCircle2 size={14} /> : cloudBridgeOnline === false ? <XCircle size={14} /> : <Loader2 size={14} className="animate-spin" />)
              : (bridgeAlive === true ? <CheckCircle2 size={14} /> : bridgeAlive === false ? <XCircle size={14} /> : <Loader2 size={14} className="animate-spin" />)}
            <span className="font-medium">
              {iOSMode && cloudBridgeOnline === null && 'Checking Cloud Relay…'}
              {iOSMode && cloudBridgeOnline === true && 'iPad mode — Cloud Relay connected. Receipts will route through your branch Bridge.'}
              {iOSMode && cloudBridgeOnline === false && 'iPad mode — No Bridge online at this branch. Open /printer-setup → Step 5b on a PC/Mac to register one.'}
              {!iOSMode && bridgeAlive === true && 'Print Bridge connected — silent printing is ON.'}
              {!iOSMode && bridgeAlive === false && 'Print Bridge not running on this PC. (Free 5-min install — silent print, no dialog.)'}
              {!iOSMode && bridgeAlive === null && 'Checking print bridge…'}
            </span>
          </div>
          {!iOSMode && bridgeAlive === false && (
            <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={downloadBridge} data-testid="download-bridge-btn">
              <Download size={12} className="mr-1" />Download Bridge
            </Button>
          )}
          {iOSMode && (
            <Button size="sm" variant="outline" className="h-7 rounded-lg text-xs" onClick={() => window.open('/printer-setup', '_blank')} data-testid="ipad-open-setup-btn">
              <ExternalLink size={12} className="mr-1" />Setup
            </Button>
          )}
        </div>

        {/* iter602 — Device-aware compatibility tip. The bridge only
            installs on Windows/Mac/Linux desktops. On iPad/iPhone/
            Android the cashier will see the OS print dialog (one tap)
            — there is no programmatic silent-print path on iOS Safari
            or Android Chrome. We explain this inline so the manager
            knows what to expect per branch. */}
        {(device === 'ipad' || device === 'iphone' || device === 'android') && (
          <div className="rounded-xl bg-info/5 border border-info/30 p-3 text-[11px] text-foreground" data-testid="device-tip-mobile">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-info shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <p className="font-semibold text-info">You&apos;re on {device === 'ipad' ? 'iPad' : device === 'iphone' ? 'iPhone' : 'Android'} — silent printing isn&apos;t possible here.</p>
                <p>Tapping <strong>Print</strong> will open the system print dialog. If your thermal printer supports <strong>AirPrint</strong> (most Star &amp; Epson models do), select it once — from then on iOS remembers and the dialog is just a single tap.</p>
                <p className="text-muted-foreground">For 100% silent printing on this branch, use a Windows/Mac PC as the cashier device and install the Print Bridge there.</p>
              </div>
            </div>
          </div>
        )}
        {(device === 'mac' || device === 'windows' || device === 'desktop') && bridgeAlive === false && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900" data-testid="device-tip-desktop">
            <div className="flex items-start gap-2">
              <Info size={14} className="shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <p className="font-semibold">Silent print not active on this {device === 'mac' ? 'Mac' : 'PC'}.</p>
                <ol className="list-decimal ml-4 space-y-0.5">
                  <li>Click <strong>Download Bridge</strong> above to get <code>smartoerp_print_bridge.py</code>.</li>
                  <li>On {device === 'windows' ? 'Windows: install Python from python.org and double-click start-bridge.bat' : 'Mac: open Terminal and run python3 smartoerp_print_bridge.py'}.</li>
                  <li>Leave it running. Refresh this page. The banner above turns green.</li>
                </ol>
                <p className="text-muted-foreground">Prefer Chrome&apos;s kiosk-printing flag? Launch Chrome with <code>--kiosk-printing</code> and set your thermal printer as default — the dialog auto-confirms in 1 second.</p>
              </div>
            </div>
          </div>
        )}

        {/* Help panel — collapsible, opens to first section by default. */}
        <PrinterSettingsHelpPanel />

        {/* iter616 — Branch quick-actions: branch-level default print
            mode (new printers inherit this) + Training mode toggle
            (force every print at this branch to the OS dialog so new
            staff see what would print before paper moves). Shown when
            the form has a specific branch selected. */}
        {selectedBranchId && selectedBranch && (
          <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="branch-quick-actions">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Zap size={13} className="text-amber-600 shrink-0" />
                <span className="text-[12px] font-semibold text-amber-900 truncate">
                  Branch quick-actions · {selectedBranch.name}
                </span>
              </div>
              <button
                type="button"
                onClick={toggleTrainingMode}
                data-testid="toggle-training-mode-btn"
                className={`text-[11px] font-semibold rounded-full px-3 py-1 transition-colors ${
                  trainingActive
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-white border border-amber-300 text-amber-900 hover:bg-amber-100'
                }`}
                title={trainingActive ? 'Training mode is ON. Click to disable.' : 'Enable training mode for 60 min.'}
              >
                {trainingActive ? '🎓 Training ON — click to disable' : '🎓 Enable Training mode (60 min)'}
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-[11px] text-amber-900 shrink-0">Default print mode for new printers at this branch:</Label>
              <Select value={branchDefaultMode} onValueChange={updateBranchDefaultMode}>
                <SelectTrigger className="h-7 text-[11px] w-44 rounded-lg bg-white" data-testid="branch-default-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (recommended)</SelectItem>
                  <SelectItem value="silent">Silent (bridge required)</SelectItem>
                  <SelectItem value="dialog">Dialog (always show OS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* iter618 — Surface the branch's receipt-language setting
                here so managers don't have to open a sale to see it.
                Read-only here; the live toggle is in ReceiptPreview. */}
            <div className="flex items-center gap-2 flex-wrap text-[11px] text-amber-900" data-testid="branch-receipt-language-info">
              <Label className="text-[11px] text-amber-900 shrink-0">Default receipt language at this branch:</Label>
              <span className="font-semibold bg-white border border-amber-300 rounded-lg px-2 py-0.5">
                {(selectedBranch.receipt_language || 'en').toUpperCase()}
                {selectedBranch.receipt_bilingual && ` + ${(selectedBranch.receipt_second_language || 'ar').toUpperCase()}`}
              </span>
              <span className="text-[10px] text-amber-700/80">
                (cashier can adjust per print from the Receipt preview)
              </span>
            </div>

            {/* iter629 — Header/footer & receipt logo now live in
                Settings → Branding. Keep this section pointer-only so
                cashiers don't accidentally type into POS dialog. */}
            <div className="flex items-center justify-between gap-2 flex-wrap p-2 rounded bg-stone-50 border border-stone-200" data-testid="branding-link-banner">
              <div className="text-[11px] text-stone-700">
                <strong>Receipt logo + header/footer</strong> are configured in <strong>Branding Settings</strong>.
              </div>
              <a href="/branding" target="_blank" rel="noreferrer"
                 className="text-[11px] font-semibold text-indigo-700 hover:underline"
                 data-testid="branding-link">
                Open Branding Settings ↗
              </a>
            </div>
            {trainingActive && (
              <p className="text-[10px] text-emerald-700 font-medium">
                Training active until {new Date(trainingUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Every print at this branch opens the OS dialog regardless of the printer&apos;s saved mode.
              </p>
            )}
          </div>
        )}

        {/* Printer Form */}
        <div className="bg-muted/50 dark:bg-muted rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-sm">{editingPrinter ? 'Edit Printer' : 'Add New Printer'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Printer Name</Label>
              <Input
                placeholder="e.g., Main Branch Cashier"
                value={printerForm.name}
                onChange={(e) => setPrinterForm({ ...printerForm, name: e.target.value })}
                className="rounded-lg"
                data-testid="printer-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={printerForm.type} onValueChange={(v) => setPrinterForm({ ...printerForm, type: v })}>
                <SelectTrigger className="rounded-lg" data-testid="printer-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receipt">Cashier / Receipt</SelectItem>
                  <SelectItem value="kitchen">Kitchen (KOT)</SelectItem>
                  <SelectItem value="bar">Bar (Drinks)</SelectItem>
                  <SelectItem value="label">Label</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* iter606 — optional Station name for kitchen/bar printers
                so a single branch can host multiple lanes (Cold Prep,
                Hot Grill, Pizza, Cocktails, etc.). Only shown for the
                two prep roles since "receipt" and "label" don't need it. */}
            {(printerForm.type === 'kitchen' || printerForm.type === 'bar') && (
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  Station <span className="text-muted-foreground font-normal">(optional — leave blank if this branch has only one {printerForm.type})</span>
                </Label>
                <Input
                  placeholder={printerForm.type === 'kitchen' ? 'e.g., Cold Prep, Hot Grill, Pizza' : 'e.g., Main Bar, Cocktails'}
                  value={printerForm.station || ''}
                  onChange={(e) => setPrinterForm({ ...printerForm, station: e.target.value })}
                  className="rounded-lg"
                  data-testid="printer-station-input"
                />
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Branch</Label>
              <Select
                value={printerForm.branch_id || '__all__'}
                onValueChange={(v) => setPrinterForm({ ...printerForm, branch_id: v === '__all__' ? null : v })}
              >
                <SelectTrigger className="rounded-lg" data-testid="printer-branch-select">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All branches (shared)</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">IP Address</Label>
              <Input
                placeholder="192.168.1.100"
                value={printerForm.ip_address}
                onChange={(e) => setPrinterForm({ ...printerForm, ip_address: e.target.value })}
                className="rounded-lg"
                data-testid="printer-ip-input"
              />
            </div>
            <div>
              <Label className="text-xs">Port</Label>
              <Input
                type="number"
                value={printerForm.port}
                onChange={(e) => setPrinterForm({ ...printerForm, port: parseInt(e.target.value) || 9100 })}
                className="rounded-lg"
                data-testid="printer-port-input"
              />
            </div>
            <div>
              <Label className="text-xs">Paper Width</Label>
              <Select value={printerForm.paper_width} onValueChange={(v) => setPrinterForm({ ...printerForm, paper_width: v })}>
                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58mm">58mm</SelectItem>
                  <SelectItem value="80mm">80mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Print mode</Label>
              <Select value={printerForm.print_mode || 'auto'} onValueChange={(v) => setPrinterForm({ ...printerForm, print_mode: v })}>
                <SelectTrigger className="rounded-lg" data-testid="printer-print-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (bridge → fallback to dialog)</SelectItem>
                  <SelectItem value="silent">Silent (bridge only — needs install)</SelectItem>
                  <SelectItem value="dialog">Dialog (always OS print dialog)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Copies</Label>
              <Input
                type="number"
                min="1"
                max="5"
                value={printerForm.copies}
                onChange={(e) => setPrinterForm({ ...printerForm, copies: parseInt(e.target.value) || 1 })}
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch
                  checked={printerForm.is_default}
                  onCheckedChange={(v) => setPrinterForm({ ...printerForm, is_default: v })}
                />
                <Label className="text-xs">Default for this role+branch</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={printerForm.auto_print}
                  onCheckedChange={(v) => setPrinterForm({ ...printerForm, auto_print: v })}
                />
                <Label className="text-xs">Auto-print on checkout</Label>
              </div>
            </div>
            <div className="flex gap-2">
              {editingPrinter && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-lg"
                  onClick={() => {
                    setEditingPrinter(null);
                    setPrinterForm({
                      name: '', type: 'receipt', station: '', ip_address: '', port: 9100,
                      paper_width: '80mm', is_default: false, auto_print: false, copies: 1,
                      branch_id: null, print_mode: 'auto',
                    });
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                className="rounded-lg bg-primary hover:bg-primary/90"
                onClick={savePrinter}
                disabled={!printerForm.name}
                data-testid="save-printer-btn"
              >
                {editingPrinter ? 'Update' : 'Add Printer'}
              </Button>
            </div>
          </div>
        </div>

        {/* Printer List */}
        <div className="space-y-2">
          <h3 className="font-semibold text-sm">Configured Printers ({printers.length})</h3>
          {printers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Printer size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No printers configured</p>
              <p className="text-xs">Add a printer above to start printing receipts</p>
            </div>
          ) : (
            printers.map((p) => {
              const branchName = p.branch_id
                ? (branches.find(b => b.id === p.branch_id)?.name || '(branch removed)')
                : 'All branches';
              const pingState = pingResults[p.id];
              return (
              <div
                key={p.id}
                className="flex items-center gap-3 p-3 bg-white dark:bg-muted border rounded-xl"
                data-testid={`printer-${p.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeBadge(p.type)}`}>
                  {typeIcon(p.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{p.name}</span>
                    {p.is_default && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-semibold">
                        Default
                      </span>
                    )}
                    <span className={`text-[9px] px-1.5 py-0.5 ${typeBadge(p.type)} rounded-full capitalize font-semibold`}>
                      {typeLabel(p.type)}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-full">
                      {branchName}
                    </span>
                    {pingState === 'ok' && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold flex items-center gap-1">
                        <CheckCircle2 size={9} /> Online
                      </span>
                    )}
                    {pingState === 'fail' && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full font-semibold flex items-center gap-1">
                        <XCircle size={9} /> Unreachable
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {p.station ? <span className="font-semibold text-foreground">Station: {p.station} · </span> : null}
                    {p.ip_address || 'No IP set'}{p.port ? `:${p.port}` : ''} · {p.paper_width} · {p.copies} {p.copies === 1 ? 'copy' : 'copies'}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-lg text-info"
                    onClick={() => handlePing(p)}
                    title="Ping printer"
                    disabled={pingingId === p.id}
                    data-testid={`ping-printer-${p.id}`}
                  >
                    {pingingId === p.id ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-lg"
                    onClick={() => testPrinter(p.id)}
                    title="Print test ticket"
                    data-testid={`test-printer-${p.id}`}
                  >
                    <Printer size={13} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-lg"
                    onClick={() => {
                      setEditingPrinter(p);
                      setPrinterForm({
                        name: p.name,
                        type: p.type,
                        station: p.station || '',
                        ip_address: p.ip_address || '',
                        port: p.port || 9100,
                        paper_width: p.paper_width || '80mm',
                        is_default: p.is_default || false,
                        auto_print: p.auto_print || false,
                        copies: p.copies || 1,
                        branch_id: p.branch_id || null,
                        print_mode: p.print_mode || 'auto',
                      });
                    }}
                  >
                    <Edit size={13} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-lg text-red-400"
                    onClick={() => deletePrinter(p.id)}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
              );
            })
          )}
        </div>
      </DialogContent>

      {/* Quick branch setup — opened from the header button. The key
          prop forces a fresh mount each time so the form resets
          without us needing an effect (which the strict lint rejects). */}
      <QuickPrinterSetupDialog
        key={showQuickSetup ? 'qs-open' : 'qs-closed'}
        open={showQuickSetup}
        onOpenChange={setShowQuickSetup}
        onCreated={() => {
          try { window.dispatchEvent(new CustomEvent('printers:reload')); } catch { /* */ }
        }}
      />
    </Dialog>
  );
}
